import { defineStore } from "pinia";
import moment, { Moment } from 'moment';
import io,  { Manager } from 'socket.io-client';

import { Station } from "~/api/models/station.model";
import { StationEvent } from "~/api/models/station-event.model";
import { useStationStore } from "~/store/station.store";
import { useAuthStore } from "~/store/auth.store";
import { Measure } from "~/api/models/measure.model";
import { DashboardStoreSocketError, StationDetailsStoreError } from "~/exceptions/store.error";
import { StationStats } from "~/api/models/station-stats.model";
import { StationGroup } from "~/api/models/station-group.model";
import { useConfigStore } from "./configuration.store";


export const useStationDetailStore = defineStore('stationDetails', {

    state: () => ({
        station: null as Station,
        events: [] as StationEvent[],
        measures: [] as Measure[],
        stats: null as StationStats,
        since: moment().subtract(1, 'months'),
        before: moment(),
        loading: false,
        now: moment(),
        socket: null as Manager
    }),

    getters: {        
    },

    actions: {

        initSocket(): void {

            if(this.socket) {
                this.socket.close();
                this.socket = null;
            }

            try {
                const authStore = useAuthStore();
                const group: StationGroup = useStationStore().group;
    
                const socketEndpoint: string = useConfigStore().socketEndpoint;
                
                this.socket = io(socketEndpoint, {
                    query: {
                        topic: `stations.${group?.name}.${this.station?.id}`,
                        token: authStore.accessToken,
                    },
                    withCredentials: true,
                });
    
                this.socket.on("measures", () => this.refresh());
            } catch(error) {
                throw new DashboardStoreSocketError('Socket could not be initialized', error);
            }


        },

        async loadMeasures(since: Moment, before: Moment) {

            if(!this.station) {
                throw new StationDetailsStoreError(`Cannot load measures. No station is selected`);
            }

            this.measures = await this.$stationService.getMeasures(this.station?.id, since, before);
        },

        async loadStats(since: Moment, before: Moment) {

            if(!this.station) {
                throw new StationDetailsStoreError(`Cannot load measures. No station is selected`);
            }

            this.stats = await this.$stationService.getStationStats(this.station?.id, since, before);
        },

        async loadEvents(since: Moment, before: Moment) {

            if(!this.station) {
                throw new StationDetailsStoreError(`Cannot load measures. No station is selected`);
            }

            this.events = await this.$stationService.getStationEvents(this.station?.id, since, before);
        },

        async setCurrentStation(stationId: string): Promise<void> {
            if(!stationId) {
                throw new StationDetailsStoreError(`Cannot load station with id null or undefined`);
            }

            const stations = useStationStore().stations;
            const station: Station = stations.find(s => s?.id === stationId);

            if(!station) {
                throw new StationDetailsStoreError(`Station with id ${stationId} does not exists`);
            }
            
            this.station = station;
        },

        setTimerange(since: Moment, before: Moment) {

            if(!since || !before) {
                throw new StationDetailsStoreError(`Could not set datetime. One or more param is null or undefined`);
            }

            if(before.isBefore(this.since)) {
                throw new StationDetailsStoreError(`Could not set datetime. Parameter before (${before.format('LLL')}) should be after parameter since (${since.format('LLL')}`);
            }

            this.since = since;
            this.before = before;
        },
        
        async refresh(): Promise<void> {

            if(!this.station) {
                throw new StationDetailsStoreError(`Cannot refresh. No station is selected`);
            }

            this.measures = null;
            this.events = null;
            this.stats = null;

            this.station = await this.$stationService.getStationDetails(this.station.id);
            await this.loadMeasures(this.since, this.before);
            await this.loadEvents(this.since, this.before);

        },
    }
});